import { PopperPlacementType } from '@mui/material'
import {
  CARE_PLAN_TAB_ID,
  FEED_TAB_ID,
  MEDICAL_RECORD_TAB_ID,
  PROGRESS_TAB_ID,
} from '../dashboard/tabNavigationContext'
import { TOUR_ANCHORS, tourSelector } from './anchors'
import type { TourExample } from './tourExample'

export type TourStepDefinition = {
  id: string
  title: string
  body: string
  // Absent: a centred card with no spotlight.
  selector?: string
  pickLast?: boolean
  // Opened first; the step is dropped while that tab is hidden.
  tab?: string
  // Dropped while that tab is hidden, but not opened.
  requiresTab?: string
  placement?: PopperPlacementType
  // The pointer can hover the target; clicks never reach it, apart from `opensMenu`.
  interactive?: boolean
  // With `interactive`: a click on the target reaches the page, and so does a click in the menu it opens.
  opensMenu?: boolean
  // Sets the care team chat open/closed for this step, and closes it on leaving.
  opensCareTeamChat?: boolean
  // A local sample, drawn only while this step is showing and the real thing is missing.
  example?: TourExample
}

export const TOUR_STEPS: TourStepDefinition[] = [
  {
    id: 'welcome',
    title: 'Welcome to the new Advanced Dashboard!',
    body: 'Most of it works like the Standard dashboard you know and LOVE. So, this quick tour mostly shows what’s new. You can replay it any time from the ? in the top bar.',
  },
  {
    id: 'tabs',
    title: 'Your tabs',
    body: 'Two of the tabs are the same as on Standard: Feed, and Medical record (i.e., Records). Care plan is new and is still a work in progress - feel free to ignore it for now :)',
    selector: tourSelector(TOUR_ANCHORS.tabBar),
    placement: 'top',
    example: 'feed',
  },
  {
    id: 'feed',
    title: 'The Feed',
    body: 'Works like Standard’s: the patient’s messages among their activity, with the composer underneath.',
    selector: tourSelector(TOUR_ANCHORS.tabContent(FEED_TAB_ID)),
    tab: FEED_TAB_ID,
    placement: 'left-start',
    example: 'feed',
  },
  {
    id: 'feed-filter',
    title: 'NEW: Filter!',
    body: 'You will only see messages here that were sent by the patient to your role (therapist, nutritionist, etc.), the patient’s general activity and your role’s tracks (e.g., lesson completions). Use Filter to add other roles’ activity, or to show messages only.',
    selector: tourSelector(TOUR_ANCHORS.feedFilter),
    tab: FEED_TAB_ID,
    placement: 'bottom-start',
    example: 'feed',
  },
  {
    id: 'feed-colors',
    title: 'Toto, I got a feeling we are not in Kansas anymore...',
    body: 'When you add progress from other tracks, they will be shown in different colors to make it easy to tell them apart. Each role has its own color (therapists green, nutritionists blue, and so on). Other roles’ activity is tinted the same way and folded into one line you can open. Your own role’s activity stays white.',
    selector: tourSelector(TOUR_ANCHORS.tabContent(FEED_TAB_ID)),
    tab: FEED_TAB_ID,
    placement: 'left-start',
    example: 'feed',
  },
  {
    id: 'feed-row-actions',
    title: 'More on every row',
    body: 'Hover a row for its ⋯. From there you can forward it to the care team chat, or comment on the patient’s activity in their chat.',
    selector: `${tourSelector(TOUR_ANCHORS.feedRow)}, ${tourSelector(TOUR_ANCHORS.feedMessage)}`,
    pickLast: true,
    tab: FEED_TAB_ID,
    placement: 'top',
    interactive: true,
    opensMenu: true,
    example: 'feed',
  },
  {
    id: 'medical-record-tab',
    title: 'Onto the Medical record',
    body: 'The tabs along the bottom switch the page. This one opens Medical record, this is the same as Standard’s Records, but redesigned for Advanced and YOU!',
    selector: tourSelector(TOUR_ANCHORS.tab(MEDICAL_RECORD_TAB_ID)),
    tab: MEDICAL_RECORD_TAB_ID,
    placement: 'top',
  },
  {
    id: 'medical-record',
    title: 'Medical record',
    body: 'Same as Standard’s Records: basic details, emergency contact, billing, consent documents and more. Cards save a moment after you stop typing, so there’s no Save button.',
    selector: tourSelector(TOUR_ANCHORS.tabContent(MEDICAL_RECORD_TAB_ID)),
    tab: MEDICAL_RECORD_TAB_ID,
    placement: 'left-start',
  },
  {
    id: 'participant-history',
    title: 'Participant history',
    body: 'Notes and history live here, as in Standard, and you can log a new entry from the top of the card.',
    selector: tourSelector(TOUR_ANCHORS.tabContent(MEDICAL_RECORD_TAB_ID), TOUR_ANCHORS.panel('participantHistory')),
    tab: MEDICAL_RECORD_TAB_ID,
    placement: 'left-start',
    example: 'event-log',
  },
  {
    id: 'care-team-chat-button',
    title: 'Open care team chat',
    body: 'Open the blue Care team chat tab on the divider beside the patient record, to the left of the patient’s name panel. It opens a side panel you can resize by dragging its left edge, or expand to fill the workspace.',
    selector: tourSelector(TOUR_ANCHORS.careTeamChatButton),
    tab: FEED_TAB_ID,
    placement: 'left',
    opensCareTeamChat: false,
  },
  {
    id: 'care-team-chat',
    title: 'Care team chat',
    body: 'Full care team is the group thread for the assigned care team, excluding care coordinators. A name under that is a private chat with that one provider — ones you already have are listed, and the rest you can start. The patient doesn’t see any of it. You can also forward feed items here, but check with the patient, and with our policies, before you share something sensitive.',
    selector: tourSelector(TOUR_ANCHORS.careTeamChat),
    tab: FEED_TAB_ID,
    placement: 'left-start',
    opensCareTeamChat: true,
    example: 'care-team-member',
  },
  {
    id: 'progress',
    title: '[WIP] Progress',
    body: 'This is where you, together with the rest of the care team, will track the patient’s progress across their tracks. It’s still being built, so what you see here will change.',
    selector: tourSelector(TOUR_ANCHORS.tab(PROGRESS_TAB_ID)),
    requiresTab: PROGRESS_TAB_ID,
    placement: 'top',
  },
  {
    id: 'care-plan',
    title: '[WIP] Care plan',
    body: 'This is where you, with the rest of the care team, will plan the patient’s care together. Each role keeps its own plan, and the psychiatrist’s master plan lives here. It’s still being built, so expect changes.',
    selector: tourSelector(TOUR_ANCHORS.tab(CARE_PLAN_TAB_ID)),
    requiresTab: CARE_PLAN_TAB_ID,
    placement: 'top',
  },
  {
    id: 'caseload',
    title: 'Your Advanced caseload',
    body: 'Only patients whose Advanced care team you’re on. Those who need attention, with a stalled track or quiet the longest, come first. The pills filter it; the second row by unread care-team chat and lab results.',
    selector: tourSelector(TOUR_ANCHORS.caseload),
    placement: 'right-start',
  },
  {
    id: 'caseload-view',
    title: 'All, Therapist, Nutritionist, Psychiatrist',
    body: 'These don’t change who’s listed, only the flags on each row. Therapist shows Standard’s flags. Nutritionist shows stalled nutrition tracks, risk and unread messages. Psychiatrist shows Standard’s flags and lab results a psychiatrist hasn’t opened yet. All shows everything. Therapists and nutritionists start on their own view, everyone else on All, and it remembers your pick.',
    selector: tourSelector(TOUR_ANCHORS.caseloadView),
    placement: 'right',
  },
  {
    id: 'info-area',
    title: 'Patient at a glance, mostly like on Standard',
    body: 'Tags, therapy, a quick note, tracks, PHQ-9 and GAD-7 trends, and their details. Symptom surveys are next.',
    selector: tourSelector(TOUR_ANCHORS.infoArea),
    placement: 'left-start',
  },
  {
    id: 'symptom-surveys',
    title: 'Symptom surveys',
    body: 'Care team roles enabled in Advanced config use + to assign symptom surveys once or on repeat. Current and scheduled surveys are under Active; surveys with only past completions are under Completed. Its badge counts past completions; the recurrence arrows show the repeat interval in days. Open a survey for its next availability date and completion chart. Switch to Table for scores and recorded completion times; select a completion for its individual answers. Results refresh every 15 seconds. Prescribing actions sit beside the close icon. Unassigning stops repeats.',
    selector: tourSelector(TOUR_ANCHORS.symptomSurveys),
    placement: 'left-start',
    interactive: true,
    example: 'symptom-survey',
  },
  {
    id: 'care-team',
    title: 'Advanced care team',
    body: 'Advanced keeps its own care team, next to Standard’s. To work with a patient here, a provider has to be on both: Standard’s care team, and this one. Use the pencil to add someone.',
    selector: tourSelector(TOUR_ANCHORS.careTeam),
    placement: 'left',
  },
]

export const selectTourSteps = (
  steps: TourStepDefinition[],
  hasTab: (tabId: string) => boolean
): TourStepDefinition[] =>
  steps.filter((step) => [step.tab, step.requiresTab].every((tabId) => !tabId || hasTab(tabId)))
